/**
 * Learner HITL lifecycle inside the active Companion:
 * restore → request → wait → human session → durable handback receipt.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HitlRequestModal from './HitlRequestModal';
import HitlWaitingBanner from './HitlWaitingBanner';
import HitlLiveSession from './HitlLiveSession';
import HitlReceipt from './HitlReceipt';
import {
  CodeSnapshot,
  Escalation,
  EscalationStatusResp,
  hitlApi,
  isHitlUnavailable,
} from '../../services/hitl';

const HITL_AMBER = '#B8600B';
const HITL_AMBER_LIGHT = '#F5E6D8';

export type HitlPaneState =
  | { kind: 'restoring' }
  | { kind: 'unavailable'; backendDisabled: boolean }
  | { kind: 'idle' }
  | { kind: 'modal' }
  | { kind: 'waiting'; escalationId: string }
  | { kind: 'live'; sessionId: string }
  | { kind: 'receipt'; sessionId: string };

interface Props {
  ensureTeachingSession: () => Promise<string>;
  lectureId?: string;
  codeSnapshot?: CodeSnapshot;
  getCodeSnapshot?: () => CodeSnapshot | undefined;
  onAgentPausedChange?: (paused: boolean) => void;
}

function stateFromEscalation(escalation: Escalation): HitlPaneState {
  if (escalation.status === 'waiting') {
    return { kind: 'waiting', escalationId: escalation.id };
  }
  if (escalation.status === 'assigned') {
    if (!escalation.session_id) {
      return { kind: 'waiting', escalationId: escalation.id };
    }
    return escalation.session_status === 'ended'
      ? { kind: 'receipt', sessionId: escalation.session_id }
      : { kind: 'live', sessionId: escalation.session_id };
  }
  if (escalation.status === 'resolved' && escalation.session_id) {
    return { kind: 'receipt', sessionId: escalation.session_id };
  }
  return { kind: 'idle' };
}

export function shouldPauseCompanion(state: HitlPaneState): boolean {
  return (
    state.kind === 'restoring' ||
    (state.kind === 'unavailable' && !state.backendDisabled) ||
    state.kind === 'waiting' ||
    state.kind === 'live'
  );
}

const HitlPane: React.FC<Props> = ({
  ensureTeachingSession,
  lectureId,
  codeSnapshot,
  getCodeSnapshot,
  onAgentPausedChange,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<HitlPaneState>({ kind: 'restoring' });
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const restoreAttemptRef = useRef(0);
  const readCodeSnapshot = useCallback(
    () => getCodeSnapshot?.() ?? codeSnapshot,
    [codeSnapshot, getCodeSnapshot],
  );

  const restoreCurrent = useCallback(async () => {
    const attempt = ++restoreAttemptRef.current;
    setState({ kind: 'restoring' });
    setRestoreError(null);
    try {
      const escalation = await hitlApi.getCurrent();
      if (attempt !== restoreAttemptRef.current) return;
      setState(escalation ? stateFromEscalation(escalation) : { kind: 'idle' });
    } catch (error) {
      if (attempt !== restoreAttemptRef.current) return;
      const backendDisabled = isHitlUnavailable(error);
      setRestoreError(
        backendDisabled
          ? 'Human instructor support is not enabled in this environment.'
          : 'Human instructor support could not be reached. Its status is not yet verified.',
      );
      setState({ kind: 'unavailable', backendDisabled });
    }
  }, []);

  useEffect(() => {
    void restoreCurrent();
    return () => {
      restoreAttemptRef.current += 1;
    };
  }, [restoreCurrent]);

  const agentPaused = shouldPauseCompanion(state);
  useEffect(() => {
    onAgentPausedChange?.(agentPaused);
    return () => onAgentPausedChange?.(false);
  }, [agentPaused, onAgentPausedChange]);

  const handleEscalated = useCallback((escalation: Escalation) => {
    setRestoreError(null);
    setState(stateFromEscalation(escalation));
  }, []);

  const handleStatus = useCallback((status: EscalationStatusResp) => {
    if (status.status === 'assigned' && status.session_id) {
      setState({ kind: 'live', sessionId: status.session_id });
    } else if (status.status === 'resolved' && status.session_id) {
      setState({ kind: 'receipt', sessionId: status.session_id });
    } else if (status.status === 'cancelled') {
      setState({ kind: 'idle' });
    }
  }, []);

  const handleLiveEnded = useCallback((sessionId: string) => {
    setState({ kind: 'receipt', sessionId });
  }, []);

  if (state.kind === 'restoring') {
    return (
      <div role="status" aria-live="polite" style={{ padding: 12, color: '#71717A', fontSize: 13 }}>
        Checking human support…
      </div>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <section
        aria-label="Human instructor support"
        style={{
          margin: 12,
          padding: 12,
          border: '1px solid #D4D4D8',
          borderRadius: 8,
          color: '#3F3F46',
          fontSize: 13,
        }}
      >
        <div role="alert">{restoreError}</div>
        {!state.backendDisabled && (
          <div style={{ marginTop: 6, color: '#71717A' }}>
            {t('ako.hitl_paused_verify', { name: t('companion.name') })}
          </div>
        )}
        <button
          type="button"
          onClick={() => void restoreCurrent()}
          style={{
            marginTop: 10,
            minHeight: 40,
            padding: '8px 12px',
            border: '1px solid #A1A1AA',
            borderRadius: 6,
            background: '#fff',
            fontFamily: 'inherit',
            fontWeight: 600,
          }}
        >
          Check again
        </button>
      </section>
    );
  }

  return (
    <section aria-label="Human instructor support" style={{ padding: 12 }}>
      {restoreError && (
        <div role="alert" style={{ color: '#B91C1C', fontSize: 12, marginBottom: 8 }}>
          {restoreError}
        </div>
      )}

      {state.kind === 'idle' && (
        <button
          type="button"
          onClick={() => setState({ kind: 'modal' })}
          aria-label="Escalate to a human instructor"
          style={{
            padding: '10px 14px',
            borderRadius: 6,
            border: `1px solid ${HITL_AMBER}`,
            background: HITL_AMBER_LIGHT,
            color: HITL_AMBER,
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 44,
          }}
        >
          <span aria-hidden>↗</span>
          Ask a human instructor
        </button>
      )}

      <HitlRequestModal
        isOpen={state.kind === 'modal'}
        onClose={() => setState({ kind: 'idle' })}
        onEscalated={handleEscalated}
        ensureTeachingSession={ensureTeachingSession}
        lectureId={lectureId}
        getCodeSnapshot={readCodeSnapshot}
      />

      {state.kind === 'waiting' && (
        <HitlWaitingBanner
          escalationId={state.escalationId}
          onStatus={handleStatus}
          onCancelled={() => setState({ kind: 'idle' })}
        />
      )}

      {state.kind === 'live' && (
        <HitlLiveSession
          sessionId={state.sessionId}
          onEnded={handleLiveEnded}
          getCodeSnapshot={readCodeSnapshot}
        />
      )}

      {state.kind === 'receipt' && (
        <HitlReceipt
          sessionId={state.sessionId}
          onDismiss={() => setState({ kind: 'idle' })}
        />
      )}
    </section>
  );
};

export { stateFromEscalation };
export default HitlPane;
